import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarAlt,
  FaUser,
} from "react-icons/fa";
import PageHeader from "../../../shared/PageHeader";
import Spinner from "../../../shared/Spinner";
import { formatDateTime } from "../../../utils/FormateDateTime";
import { useTranslation } from "react-i18next";
import useFetchData from "../../../hooks/UseFetchData";

const AppointmentHistoryPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, loading, executeFetch } = useFetchData();
  const [historyList, setHistoryList] = useState([]);

  useEffect(() => {
    if (id) {
      executeFetch("POST", `/v3/patientHistory/appointment/${id}`, {});
    }
  }, [id, executeFetch]);

  useEffect(() => {
    if (data) setHistoryList(data?.history || []);
  }, [data]);

  if (loading) {
    return <div className="text-center py-10">{t("Loading...")}</div>;
  }

  return (
    <div className="main-page-container !overflow-y-auto scrollbar">
      <PageHeader
        title={t("Appointment History")}
        iconSize={20}
        Icon={i18n.language === "en" ? FaArrowLeft : FaArrowRight}
        onIconClick={() => navigate(-1)}
      />

      <div className="flex flex-col gap-4 mt-3">
        {historyList.length > 0 ? (
          historyList.map((item, index) => (
            <HistoryCard
              key={index}
              data={item}
              index={index}
              total={historyList.length}
            />
          ))
        ) : (
          <p className="text-center text-gray-600 mt-10">
            {t("No appointment history found.")}
          </p>
        )}
      </div>
    </div>
  );
};

const HistoryCard = ({ data, index, total }) => {
  const isLast = index === total - 1;
  const { t } = useTranslation();

  const statusColor =
    {
      accepted: "bg-green-100 text-green-700 border-green-300",
      declined: "bg-red-100 text-red-700 border-red-300",
      tentative: "bg-yellow-100 text-yellow-700 border-yellow-300",
      "needs-action": "bg-blue-100 text-blue-700 border-blue-300",
    }[data?.status?.toLowerCase()] ||
    "bg-gray-100 text-gray-700 border-gray-300";

  return (
    <div className="relative">
      {!isLast && (
        <div className="absolute left-6 top-10 bottom-0 w-[2px] bg-gray-200"></div>
      )}

      <div className="relative z-10 ml-4 flex items-start gap-4">
        <div className="w-8 h-8 flex items-center justify-center bg-primary text-white font-semibold rounded-full shrink-0">
          {index + 1}
        </div>

        <Card
          pt={{
            body: { className: "!p-3" },
            content: { className: "!p-0" },
          }}
          className="w-full border border-gray-200 rounded-xl bg-white hover:shadow-sm transition-all"
        >
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span
                className={`text-sm px-2 py-0.5 rounded-full border font-medium capitalize ${statusColor}`}
              >
                {data?.status || t("Unknown")}
              </span>
              <span className="flex items-center text-xs text-gray-500">
                <FaCalendarAlt className="mr-1 text-gray-400" />
                {formatDateTime(new Date(data?.created_at), "PPP p")}
              </span>
            </div>

            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1 text-sm text-gray-700">
              <Info label={t("Notes")} value={data?.action_reason} />

              <Info label={t("Patient")} value={data?.patient_name} />
              <Info label={t("Practitioner")} value={data?.practitioner_name} />
              <Info label={t("Action By")} value={data?.action_performed_by?.name} />
              <Info label={t("Role")} value={data?.action_performed_by?.role} />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className="truncate">
    <span className="font-medium text-gray-800">{label}:</span>{" "}
    <span className="text-gray-600">{value || "-"}</span>
  </div>
);

export default AppointmentHistoryPage;
